/**
 * Связка инструмента OSRM с картой ОИ: активация по activeTool==="route", панель, API.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { Map as MapLibreMap } from "maplibre-gl";
import {
  MapLibreRouteTool,
  type RoutePoint,
  type RouteToolMode,
} from "@/components/map-libre/MapLibreRouteTool";
import {
  RoutingToolPanel,
  type NearestRankRow,
} from "@/components/map/RoutingToolPanel";
import { routingApi } from "@/services/api-v2.service";
import { useToast } from "@/hooks/use-toast";

type Props = {
  map: MapLibreMap | null;
  active: boolean;
  onClose: () => void;
};

function errMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  return "Не удалось рассчитать маршрут";
}

export function OiMapRoutingBridge({ map, active, onClose }: Props) {
  const { toast } = useToast();
  const toolRef = useRef<MapLibreRouteTool | null>(null);
  const [mode, setMode] = useState<RouteToolMode>("route");
  const [pointCount, setPointCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [routeSummary, setRouteSummary] = useState<{
    distance_m: number | null;
    duration_s: number | null;
    air_distance_m?: number | null;
  } | null>(null);
  const [nearestRows, setNearestRows] = useState<NearestRankRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const modeRef = useRef(mode);
  modeRef.current = mode;

  const runRoute = useCallback(
    async (points: RoutePoint[]) => {
      if (points.length < 2) return;
      setLoading(true);
      setError(null);
      setNearestRows([]);
      try {
        const [a, b] = points;
        const cmp = await routingApi.compare(a, b);
        if (cmp.geometry && (cmp.geometry as { type?: string }).type === "LineString") {
          toolRef.current?.setRouteLine(cmp.geometry as unknown as GeoJSON.LineString);
        } else if (cmp.road_ok) {
          const res = await routingApi.route(points);
          toolRef.current?.setRouteLine(res.geometry as unknown as GeoJSON.LineString);
        } else {
          toolRef.current?.setRouteLine(null);
        }
        if (!cmp.road_ok) {
          setError(cmp.road_error || "Дорожный маршрут недоступен");
          setRouteSummary({
            distance_m: null,
            duration_s: null,
            air_distance_m: cmp.air_distance_m,
          });
        } else {
          setRouteSummary({
            distance_m: cmp.road_distance_m ?? null,
            duration_s: cmp.road_duration_s ?? null,
            air_distance_m: cmp.air_distance_m,
          });
        }
      } catch (err) {
        const msg = errMessage(err);
        setError(msg);
        setRouteSummary(null);
        toolRef.current?.setRouteLine(null);
        toast({ title: "Маршрут", description: msg, variant: "destructive" });
      } finally {
        setLoading(false);
      }
    },
    [toast],
  );

  const computeNearest = useCallback(async () => {
    const points = toolRef.current?.getPoints() ?? [];
    if (points.length < 2) return;
    setLoading(true);
    setError(null);
    setRouteSummary(null);
    try {
      const sources = [0];
      const destinations = points.slice(1).map((_, i) => i + 1);
      const res = await routingApi.table(points, { sources, destinations });
      const durations = res.durations[0] ?? [];
      const distances = res.distances[0] ?? [];
      const rows: NearestRankRow[] = destinations.map((destIdx, col) => ({
        index: destIdx,
        duration_s: durations[col] ?? null,
        distance_m: distances[col] ?? null,
      }));
      rows.sort((a, b) => {
        const da = a.duration_s ?? Number.POSITIVE_INFINITY;
        const db = b.duration_s ?? Number.POSITIVE_INFINITY;
        return da - db;
      });
      setNearestRows(rows);
    } catch (err) {
      const msg = errMessage(err);
      setError(msg);
      setNearestRows([]);
      toast({ title: "Матрица", description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (!map) return;

    if (!active) {
      toolRef.current?.deactivate(true);
      toolRef.current = null;
      setPointCount(0);
      setRouteSummary(null);
      setNearestRows([]);
      setError(null);
      return;
    }

    const tool =
      toolRef.current ??
      new MapLibreRouteTool(map, {
        onPointsChange: (pts) => setPointCount(pts.length),
        onMapClickPoint: (_p, pts) => {
          if (modeRef.current === "route" && pts.length === 2) {
            void runRoute(pts);
          }
        },
        onExit: onClose,
      });
    toolRef.current = tool;
    tool.activate(modeRef.current);

    return () => {
      /* deactivate only when active flips false or unmount handled above */
    };
  }, [map, active, onClose, runRoute]);

  useEffect(() => {
    if (!active || !toolRef.current) return;
    toolRef.current.setMode(mode);
    toolRef.current.resetPoints();
    setRouteSummary(null);
    setNearestRows([]);
    setError(null);
    setPointCount(0);
  }, [mode, active]);

  if (!active) return null;

  return (
    <RoutingToolPanel
      mode={mode}
      onModeChange={setMode}
      pointCount={pointCount}
      loading={loading}
      routeSummary={routeSummary}
      nearestRows={nearestRows}
      error={error}
      onClear={() => {
        toolRef.current?.resetPoints();
        setRouteSummary(null);
        setNearestRows([]);
        setError(null);
        setPointCount(0);
      }}
      onComputeNearest={() => void computeNearest()}
      onClose={onClose}
    />
  );
}
